"use strict";

const ButtonControl = require('@gluenetworks/button_control');

const _ = require('lodash');

class ButtonControlRenderer {
    constructor(model) {
        this._model = model;
        this._rootElem = document.createElement("button-group-control");
        this._disabledMask = document.createElement('div');
        this._disabledMask.classList.add('disabled-mask');
        this._rootElem.appendChild(this._disabledMask);
        this._rootElem.component = this._model;
        this._buttons = [];

        this._registerListeners();
        this.render();
    }
    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/
    _shutdown(){
        let prom = new Promise((resolve /*,reject*/) => {
            this._unregisterListeners();
            this._model = undefined;
            this._buttons.forEach((button) => {
                button.shutdown();
            });
            this._buttons = undefined;
            this._rootElem.remove();
            this._rootElem = undefined;
            resolve();
        });
        return prom;
    }
    _registerListeners(){
        this._model.on("Options",this._onOptions = this._onOptions.bind(this));
        this._model.on("Disabled",this._onDisabled = this._onDisabled.bind(this));
        this._model.on("Invalid",this._onInvalid = this._onInvalid.bind(this));
        this._model.on("Value",this._onValue = this._onValue.bind(this));
        this._model.on("Visible",this._onVisible = this._onVisible.bind(this));
        this._model.on("DarkTheme",this._onDarkTheme = this._onDarkTheme.bind(this));
        this._model.on("Focus",this._onFocus = this._onFocus.bind(this));
        this._model.on("Blur",this._onBlur = this._onBlur.bind(this));
        this._model.on("SetAttribute",this._onSetAttribute = this._onSetAttribute.bind(this));
        this._rootElem.addEventListener('focus', this._onFocusEvent = this._onFocusEvent.bind(this));
        this._rootElem.addEventListener('blur', this._onBlurEvent = this._onBlurEvent.bind(this));
        this._rootElem.addEventListener('keydown', this._onKeyDownEvent = this._onKeyDownEvent.bind(this));
    }
    _unregisterListeners(){
        this._model.removeListener("Options",this._onOptions);
        this._model.removeListener("Disabled",this._onDisabled);
        this._model.removeListener("Invalid",this._onInvalid);
        this._model.removeListener("Value",this._onValue);
        this._model.removeListener("Visible",this._onVisible);
        this._model.removeListener("DarkTheme",this._onDarkTheme);
        this._model.removeListener("Focus",this._onFocus);
        this._model.removeListener("Blur",this._onBlur);
        this._model.removeListener("SetAttribute",this._onSetAttribute);
        this._buttons.forEach((button) => {
            button.removeListener('UserClick', this._onClickEvent);
        });
        this._rootElem.removeEventListener('focus', this._onFocusEvent);
        this._rootElem.removeEventListener('blur', this._onBlurEvent);
        this._rootElem.removeEventListener('keydown', this._onKeyDownEvent);
    }
    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/
    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     * @returns {Promise} An ES6 promise that lets the caller know when the component has finished rendering. If a non
     * Promise is returned the caller will assume the Renderer has completed rendering.
     */
    render(){
        let prom = new Promise((resolve/*,reject*/) => {
            this._setOptions(this._model.options);
            this._setDisabled(this._model.disabled);
            this._setValue(this._model.value);
            this._setVisible(this._model.visible);
            this._setInvalid(this._model.invalid);
            this._setDarkTheme(this._model.darkTheme);
            resolve();
        });
        return prom;
    }
    get rootElement(){
        return this.getRootElement();
    }
    getRootElement(){
        return this._rootElem;
    }
    get model(){
        return this.getModel();
    }
    getModel(){
        return this._model;
    }
    /*******************************************/
    /* END COMPONENT RENDERER METHODS */
    /* START HANDLER METHODS */
    /*******************************************/

    /**
     * Fired when the options have been updated in the model
     * @param newVal {Array}
     * @private
     */
    _onOptions(newVal){
        this._setOptions(newVal);
    }

    /**
     * Fired when the disabled state has been updated in the model
     * @param newVal {Boolean}
     * @private
     */
    _onDisabled(newVal){
        this._setDisabled(newVal);
    }

    /**
     * Fired when the invalid state been updated in the model
     * @param newVal {Boolean}
     * @private
     */
    _onInvalid(newVal){
        this._setInvalid(newVal);
    }

    /**
     * Fired when the value has been updated in the model
     * @param newVal {string|object|boolean|number}
     * @param oldVal {string|object|boolean|number}
     * @private
     */
    _onValue(newVal /*, oldVal*/){
        this._setValue(newVal);
    }

    /**
     * Fired when the visible state has been updated in the model
     * @param newVal {Boolean}
     * @private
     */
    _onVisible(newVal){
        this._setVisible(newVal);
    }

    /**
     * Fired when the darkTheme has been updated in the model
     * @param newVal {Boolean}
     * @private
     */
    _onDarkTheme(newVal){
        this._setDarkTheme(newVal);
    }

    /**
     * Fired when focus has been called in the model
     * @private
     */
    _onFocus(){
        this._setFocus(true);
    }

    /**
     * Fired when blur has been called in the model
     * @private
     */
    _onBlur(){
        this._setFocus(false);
    }

    /**
     * Fired when setAttribute has been called in the model
     * @param attrName {String} - The name of the HTML attribute to be set on the native control
     * @param attrValue {String|Undefined} - The value of the HTML attribute to be set on the native control
     * @private
     */
    _onSetAttribute(attrName, attrValue){
        this._setAttribute(attrName, attrValue);
    }

    /**
     * Fired when an inner button is clicked
     * @param event {Event}
     * @private
     */
    _onClickEvent(event){
        let oldVal = _.cloneDeep(this._model.value);
        let newVal = event.currentTarget.component.value;
        this._model.setValue(newVal, true);
        this._model._triggerUserValue(newVal, oldVal, event);
    }

    /**
     * Pass the focus event up to the model
     * @param event {Event}
     * @private
     */
    _onFocusEvent(event){
        this._model._triggerUserFocus(event);
    }

    /**
     * Pass the blur event up to the model
     * @param event {Event}
     * @private
     */
    _onBlurEvent(event){
        this._model._triggerUserBlur(event);
    }

    /**
     * Pass the blur event up to the model
     * @param event {Event}
     * @private
     */
    _onKeyDownEvent(event){
        this._model._triggerUserKeyDown(event);
    }

    /*******************************************/
    /* END HANDLER METHODS */
    /* START PRIVATE API METHODS */
    /*******************************************/

    /**
     * Set the inner buttons with the passed in array of buttons from the model
     * @param newVal {Array}
     * @private
     */
    _setOptions(newVal){
        this._buttons.forEach((button) => {
            button.removeListener('UserClick', this._onClickEvent);
            button.shutdown();
        });
        this._buttons = [];
        this._rootElem.innerHTML = '';
        this._rootElem.appendChild(this._disabledMask);
        newVal.forEach((option) => {
            let button = new ButtonControl({
                label: option.label,
                value: option.value,
                disabled: option.disabled
            });
            button.on('UserClick', this._onClickEvent = this._onClickEvent.bind(this));
            this._rootElem.appendChild(button.renderer.rootElement);
            this._buttons.push(button);
        });
    }

    /**
     * Toggle the disabled state
     * @param newVal {Boolean}
     * @private
     */
    _setDisabled(newVal){
        if (newVal === true) {
            this._rootElem.setAttribute('disabled', true);
        } else {
            this._rootElem.removeAttribute('disabled');
        }
    }

    /**
     * Toggle the invalid state
     * @param newVal {Boolean}
     * @private
     */
    _setInvalid(newVal){
        if (newVal === true) {
            this._rootElem.classList.add('invalid');
        } else {
            this._rootElem.classList.remove('invalid');
        }
    }

    /**
     * Set the inner button that matches the new value active
     * @param newVal {string|object|boolean|number}
     * @private
     */
    _setValue(newVal){
        this._buttons.forEach((button) => {
            button.active = false;
        });
        this._buttons.forEach((button) => {
            if (_.isEqual(button.value, newVal)) {
                button.active = true;
            }
        });
    }

    /**
     * Toggle the visible state
     * @param newVal {Boolean}
     * @private
     */
    _setVisible(newVal){
        if (newVal === true) {
            this._rootElem.classList.remove('hidden');
        } else {
            this._rootElem.classList.add('hidden');
        }
    }

    /**
     * Toggle or set the dark theme
     * @param newVal {Boolean}
     * @private
     */
    _setDarkTheme(newVal){
        if (newVal === true) {
            this._rootElem.classList.add('dark');
        } else {
            this._rootElem.classList.remove('dark');
        }
    }

    /**
     * Set or remove focus
     * @param newVal {Boolean}
     * @private
     */
    _setFocus(newVal){
        if (newVal === true) {
            this._rootElem.focus();
        } else {
            this._rootElem.blur();
        }
    }

    /**
     * Set an attribute on the native control
     * @param attrName {String} - The name of the HTML attribute to be set on the native control
     * @param attrValue {String|Undefined} - The value of the HTML attribute to be set on the native control
     * @private
     */
    _setAttribute(attrName, attrValue){
        if (!_.isUndefined(attrValue)) {
            this._rootElem.setAttribute(attrName, attrValue);
        } else {
            this._rootElem.removeAttribute(attrName);
        }
    }
    
    /*******************************************/
    /*  END PRIVATE API METHODS */
    /*******************************************/
}

module.exports = ButtonControlRenderer;